/**
 * Đồng hồ game và HÀM TICK CHUNG cho online lẫn offline.
 *
 * - 1 giờ game = 1 phút thực (P01); 1 ngày game = 24 giờ game (P02).
 * - Tick cố định 1 giờ game. Offline dùng đúng hàm này với
 *   heSoHieuSuat = 0,3 (P05) — hai mạch không thể lệch luật.
 * - Tua nhanh = gọi tick nhiều lần (tuaNhanh).
 *
 * Thứ tự trong 1 tick M3 (ghi rõ để test/replay ổn định):
 *  1. tongGioGame += 1
 *  2. reset nhiệm vụ hằng ngày nếu sang ngày máy mới (0 giờ giờ máy)
 *  3. thời tiết (đếm ngược M16 giờ)
 *  4. khách mua hàng (kho, tiền lãi, thống kê món; hệ số khu + thời tiết)
 *  5. quầy cà phê chi tiết 5 món (nếu mở: CH cấp 3 + người chơi cấp 3)
 *  6. danh tiếng động
 *  7. lương nhân viên ĐÃ THUÊ (× heSoHieuSuat)
 *  8. nhận giao hàng đến hạn
 *  9. đặt đơn nhập tự động theo ngưỡng
 * 10. luật chống kẹt + trừ nợ từ lãi gộp tick
 * 11. chốt tiền xu tick này
 * 12. tiến độ nhiệm vụ hằng ngày (từ số liệu tick) → thưởng
 * 13. đơn lớn: sinh mới / hết hạn
 * 14. thành tựu + sưu tập (thưởng xu cao cấp)
 * 15. XP: bán hàng, mốc bán hàng
 */
import { capNhatDanhTieng } from './danh-tieng.js';
import { layThamSoMoPhong } from './du-lieu.js';
import { caPheMoiGio, luongMoiGioGame } from './cua-hang.js';
import { xuLyGioKhach } from './khach.js';
import { datHangTuDong, kiemTraChongKet, nhanGiaoHang, nhanLoNo, traNoTuLaiGop } from './kho.js';
import {
  capNhatTienDoNhiemVu,
  kiemTraSangNgayMoi,
  ngayMayHienTai,
  xuLyDonLonTheoGio,
  type ThongTinBanHangTick,
} from './nhiem-vu.js';
import { kiemTraThanhTuu, kiemTraSuuTap } from './thanh-tuu.js';
import { capNhatThoiTiet } from './thoi-tiet.js';
import type { HamRng } from './rng.js';
import { congXp, xuLyMocBanHang } from './xp.js';
import type { DuLieuGame, TrangThaiGame } from './types.js';

export interface TuyChonTick {
  /**
   * Hệ số hiệu suất: 1 khi online, 0,3 (P05) khi offline.
   * Nhân vào lưu lượng khách, cà phê và lương.
   */
  heSoHieuSuat?: number;
  /**
   * Ngày máy (YYYY-MM-DD) cho tick này. Mặc định = ngày máy thật hiện tại.
   * Sim/offline truyền ngày ảo để nhiệm vụ hằng ngày reset đúng.
   */
  ngayMay?: string;
}

export interface KetQuaTick {
  laiGop: number;
  luong: number;
  monBan: number;
  hetHang: number;
  donGiao: number;
  donDat: number;
  traNo: number;
  /** Mã NV hằng ngày vừa hoàn thành trong tick. */
  nvHoanThanh: string[];
  /** Mã thành tựu vừa mở trong tick. */
  thanhTuuVuaMo: string[];
  /** Tên nhóm sưu tập vừa xong trong tick. */
  suuTapVuaXong: string[];
}

/** Một giờ game. Hàm duy nhất cho cả online và offline. */
export function tick(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  rng: HamRng,
  tuyChon: TuyChonTick = {},
): KetQuaTick {
  const heSoHieuSuat = tuyChon.heSoHieuSuat ?? 1;
  const ketQua: KetQuaTick = {
    laiGop: 0,
    luong: 0,
    monBan: 0,
    hetHang: 0,
    donGiao: 0,
    donDat: 0,
    traNo: 0,
    nvHoanThanh: [],
    thanhTuuVuaMo: [],
    suuTapVuaXong: [],
  };

  // 1. đồng hồ
  state.tongGioGame += 1;

  // 2. nhiệm vụ hằng ngày: reset 0 giờ giờ máy
  kiemTraSangNgayMoi(state, duLieu, tuyChon.ngayMay ?? ngayMayHienTai());

  // 3. thời tiết
  capNhatThoiTiet(state, duLieu, rng);

  // 4–5. khách + cà phê
  const kqKhach = xuLyGioKhach(state, duLieu, rng, heSoHieuSuat);
  const kqCaPhe = caPheMoiGio(state, duLieu, heSoHieuSuat);
  for (const [ma, sl] of Object.entries(kqKhach.monTheoMa)) {
    state.thongKe.monDaBan[ma] = (state.thongKe.monDaBan[ma] ?? 0) + sl;
  }
  if (kqCaPhe.coc > 0) {
    state.thongKe.monDaBan['CA_PHE'] = (state.thongKe.monDaBan['CA_PHE'] ?? 0) + kqCaPhe.coc;
    state.thongKe.tongCocCaPhe += kqCaPhe.coc;
    for (const [ten, sl] of Object.entries(kqCaPhe.theoMon)) {
      state.caPheTheoMon[ten] = (state.caPheTheoMon[ten] ?? 0) + sl;
    }
  }
  const tongMon = kqKhach.monBan + kqCaPhe.coc;
  state.thongKe.tongMonDaBan += tongMon;
  state.thongKe.soLuotHetHang += kqKhach.hetHang;
  const laiGopBanHang = kqKhach.laiGop + kqCaPhe.lai;

  // 6. danh tiếng
  capNhatDanhTieng(state, duLieu, kqKhach.hetHang);

  // 7. lương (offline cũng tính theo hiệu suất — lỗi 1 master-plan 2.2)
  const luong = luongMoiGioGame(state, duLieu) * heSoHieuSuat;

  // 8–9. giao hàng + đặt đơn mới
  const donGiao = nhanGiaoHang(state);
  const donDat = datHangTuDong(state, duLieu);

  // 10. chống kẹt
  if (kiemTraChongKet(state, duLieu)) {
    nhanLoNo(state, duLieu);
    state.thongKe.lanChoNo += 1;
  }
  const traNo = traNoTuLaiGop(laiGopBanHang, state, duLieu);

  // 11. chốt tiền: hoàn vốn hàng bán + lãi gộp − trả nợ − lương
  const laiGop = laiGopBanHang - traNo;
  state.xu += laiGop + kqKhach.vonHangBan - luong;
  state.thongKe.tongLaiGop += laiGop;
  state.thongKe.tongLuong += luong;

  // 12. tiến độ nhiệm vụ hằng ngày từ số liệu tick
  const thongTin: ThongTinBanHangTick = {
    monTheoNhom: kqKhach.monTheoNhom,
    luotTheoKieu: kqKhach.luotTheoKieu,
    doanhThu: laiGopBanHang + kqKhach.vonHangBan,
    cocCaPhe: kqCaPhe.coc,
  };
  ketQua.nvHoanThanh = capNhatTienDoNhiemVu(state, duLieu, thongTin);

  // 13. đơn lớn: sinh mới / hết hạn
  xuLyDonLonTheoGio(state, duLieu, rng);

  // 14. thành tựu + sưu tập (thưởng xu cao cấp ngay)
  ketQua.thanhTuuVuaMo = kiemTraThanhTuu(state, duLieu).map((t) => t.ma);
  ketQua.suuTapVuaXong = kiemTraSuuTap(state, duLieu);

  // 15. XP
  congXp(state, duLieu, tongMon * layThamSoMoPhong(duLieu, 'M06'));
  xuLyMocBanHang(state, duLieu);

  ketQua.laiGop = laiGop;
  ketQua.luong = luong;
  ketQua.monBan = tongMon;
  ketQua.hetHang = kqKhach.hetHang;
  ketQua.donGiao = donGiao;
  ketQua.donDat = donDat;
  ketQua.traNo = traNo;
  return ketQua;
}

/** Tua nhanh N giờ game (gộp kết quả). */
export function tuaNhanh(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  soGioGame: number,
  rng: HamRng,
  tuyChon: TuyChonTick = {},
): KetQuaTick {
  const tong: KetQuaTick = {
    laiGop: 0,
    luong: 0,
    monBan: 0,
    hetHang: 0,
    donGiao: 0,
    donDat: 0,
    traNo: 0,
    nvHoanThanh: [],
    thanhTuuVuaMo: [],
    suuTapVuaXong: [],
  };
  const n = Math.max(0, Math.floor(soGioGame));
  for (let i = 0; i < n; i++) {
    const kq = tick(state, duLieu, rng, tuyChon);
    tong.laiGop += kq.laiGop;
    tong.luong += kq.luong;
    tong.monBan += kq.monBan;
    tong.hetHang += kq.hetHang;
    tong.donGiao += kq.donGiao;
    tong.donDat += kq.donDat;
    tong.traNo += kq.traNo;
    tong.nvHoanThanh.push(...kq.nvHoanThanh);
    tong.thanhTuuVuaMo.push(...kq.thanhTuuVuaMo);
    tong.suuTapVuaXong.push(...kq.suuTapVuaXong);
  }
  return tong;
}
